<script setup lang="ts">

import { computed } from "vue";
import type { IMarketplaceViewModel } from '@app/MyMarketplace/Types/IMarketplaceViewModel';
import type { IMarketplaceCategory } from "@domain/Marketplace";

import HorizontalDragScroll from "../Common/HorizontalDragScroll.vue";
import FilterButton from "./FilterButton.vue";

const model = defineModel<IMarketplaceViewModel>( { required: true })
const emit = defineEmits( [ 'on-back' ] );
const isSearching = computed( () => model.value.activePanel == 'search' );
const isBrowsingNearest = computed( () => model.value.activePanel == 'nearest' );
const isFiltered = false;

const filteredCategories = computed( () => 
    isFiltered 
    ? model.value.categories.filter( c => c.offersCount > 0 ) 
    : model.value.categories
);

/**
 * Determines if the category is in a collapsed state
 * @param category 
 */
const isCategoryCollapsed = ( category: IMarketplaceCategory ) =>
{
    if( isSearching.value || isBrowsingNearest.value ) return true;
    const group = model.value.activeGroup;
    const categoryId = model.value.activeCategory;
    if( group == 'automotive' && category.categoryId != 'automotive' ) return true;
    if( categoryId != null && category.categoryId != categoryId ) return true;
    return false;
}

</script>

<template>
    
    <div class="flex items-center w-full
                bg-gradient-to-b from-transparent via-75% via-gray-900/40 to-transparent"> 

        <!-- BACK BUTTON -->
        <div class="w-0 overflow-hidden transition-all duration-300 translate-x-2 3xl:translate-x-4"
             :class="{
                'w-10 3xl:mr-2': model.activeGroup != null || isSearching || isBrowsingNearest || model.filteredSubCategory != null
             }">
             <IconButton name="chevron-left"
                         class="text-3xl -mr-2" 
                         @click="() => model.reset()" />
        </div>

        <!-- SEARCH RESULTS LABEL -->
        <div v-if="isSearching"
             class="pl-4"> 
            <span v-if="model.totalCount > 0">
                Found {{ model.totalCount == 1 ? '1 result' : `${model.totalCount} results` }}
            </span>
            <span v-else>No results found</span>
        </div>
        
        <!-- OFFERS NEAR ME LABEL -->
        <div v-else-if="isBrowsingNearest"
             class="pl-4"> 
            <span v-if="model.totalCount > 0">
                Found {{ model.totalCount == 1 ? '1 nearby offer' : `${model.totalCount} nearby offers` }}
            </span>
            <span v-else-if="model.hasSearchBoundary == false">Nearby offers not available</span>
            <span v-else>No nearby offers found</span>
        </div>

        <!-- FILTERED SUB CATEGORY LABEL -->
        <div v-if="model.filteredSubCategory != null"
             class="pl-4 flex items-center gap-2 3xl:gap-4 -ml-2"> 
            <Icon name="car" />
            {{ model.filteredSubCategory.displayName }}
        </div>

        <!-- CATEGORY LIST -->
        <HorizontalDragScroll :mandatory="true" 
                              class="flex-1 z-10"
                              @on-select="( categoryId: string ) => model.selectCategory( 
                                  categoryId == 'automotive' ? 'automotive' : null, 
                                  categoryId == 'automotive' ? null : categoryId 
                              )">

            <div v-for="category in filteredCategories" 
                 :key="category.categoryId"
                 :value="category.categoryId"
                 class="flex items-center cursor-pointer select-none
                        transition-all duration-300 max-w-[40%]
                        mx-1 3xl:mx-2 first:ml-2 last:mr-2 3xl:first:ml-4 3xl:last:mr-4"
                 :class="{ 
                    'pointer-events-none !max-w-0 !opacity-0 !mx-0': isCategoryCollapsed( category ),
                    'pointer-events-none uppercase !max-w-full': model.activeCategory == category.categoryId
                 }">
                 
                <div class="flex-1 flex items-center justify-center leading-tight h-0
                            w-auto min-w-[5rem] 3xl:min-w-[6rem] min-h-[4rem] 3xl:min-h-[4rem] px-3 
                            text-sm 3xl:text-lg tracking-widest rounded-sm gap-x-3"
                     :class="{ 'flex-col': category.isAutomotive && model.activeCategory != category.categoryId }">
                    
                    <!-- MY CAR ICON -->
                    <Icon v-if="category.isAutomotive"
                          name="car" />

                    <!-- CATEGORY TEXT -->
                    <div v-html="model.activeCategory != category.categoryId ? category.displayName : category.name" 
                         class="whitespace-nowrap leading-tight"
                         :class="{
                            'uppercase text-xs 3xl:text-sm font-semibold': category.isAutomotive
                         }" />

                </div>

            </div>
        
        </HorizontalDragScroll>
    
        <!-- FILTER BUTTON -->
        <FilterButton v-model="model" 
                      :is-searching="isSearching"
                      :disabled="model.activePanel == 'nearest' && model.hasSearchBoundary == false" />

    </div>

</template>
